import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import { Add as AddIcon, MedicalServicesOutlined as MedicalServicesOutlinedIcon } from '@mui/icons-material';
import { DataProviderInstance } from '../../api/DataProvider';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import Toast from '../../components/support/Toast';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_LIMITS } from '../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../components/subscription/PlanUpgradeModal';
import useCurrentPlanLabel from '../../components/subscription/useCurrentPlanLabel';
import { limitActionHint, planLimitText } from '../../components/subscription/planLockTexts';
import ProcedureOverview from './components/ProcedureOverview';
import ProcedureTable from './components/ProcedureTable';
import ProcedureFormPopup from './components/ProcedureFormPopup';
import { parseMoney, formatMoneyInput, cleanMoneyTyping } from '../../utils/moneyUtils';
import styles from './ProcedureList.module.css';

const ADD_DENIED = 'Apenas proprietários e administradores podem adicionar procedimentos';

const EMPTY_FORM = {
    label: '',
    price: '',
    duration: '00:00:00',
    description: '',
    public_schedule_enabled: false,
    public_schedule_name: '',
    public_schedule_description: ''
};

/**
 * Cadastros > Procedimentos. A página orquestra estado, permissões e a trava
 * de limite do plano; resumo, lista e formulário ficam em ./components.
 */
const ProcedureList = () => {
    const { CompanyConfig, GetCompanyProcedures, SetCompanyProcedure, UpdateCompanyProcedure, DeleteCompanyProcedure } = DataProviderInstance();
    const { hasRole } = useRoleAccess();
    const { getLimit } = useSubscriptionFeatures();
    const planLabel = useCurrentPlanLabel();
    // Aviso de limite do plano: false | 'create' | 'reactivate' (muda o que fazer no texto)
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [procedures, setProcedures] = useState([]);
    // Só até a primeira resposta: evita mostrar "nenhum procedimento" antes de carregar
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedProcedure, setSelectedProcedure] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [procedureToDelete, setProcedureToDelete] = useState(null);
    const [showConfirmReactivate, setShowConfirmReactivate] = useState(false);
    const [procedureToReactivate, setProcedureToReactivate] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Verificar se o usuário tem permissão para adicionar/editar/excluir procedimentos (COMPANY_OWNER ou COMPANY_ADMIN)
    const canManageProcedures = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isFormValid, setIsFormValid] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadProcedures();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProcedures = () => {
        GetCompanyProcedures((result) => {
            setProcedures(result || []);
            setLoading(false);
        });
    };

    // Criar, editar, inativar e reativar já buscam a lista de novo no DataProvider
    // (CompanyConfig.Procedures): a tela só acompanha essa lista depois de salvar,
    // sem uma segunda chamada
    const awaitingSyncRef = useRef(false);
    const syncedProcedures = CompanyConfig?.Procedures;
    useEffect(() => {
        if (!awaitingSyncRef.current || !Array.isArray(syncedProcedures)) return;
        awaitingSyncRef.current = false;
        setProcedures(syncedProcedures);
    }, [syncedProcedures]);

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    // Resposta comum de criar, editar, inativar e reativar: limite do plano, erro ou aviso de sucesso
    const handleSaved = (result, successMessage, limitAction) => {
        if (limitAction && result?.code === 'BLOCKED_ON_PLAN') {
            // O servidor também barra no limite (ex.: outra aba criou ou reativou antes)
            setUpgradeModalOpen(limitAction);
            return;
        }
        if (result?.error) {
            showToast('Não foi possível salvar o procedimento. Tente novamente.', 'error');
            return;
        }
        showToast(successMessage);
    };

    const activeProceduresCount = procedures.filter(p => p.Active == 1).length;

    // Limite do plano: mesma conta do servidor (só procedimentos ativos)
    const procedureLimit = getLimit(SUBSCRIPTION_LIMITS.PROCEDURES);
    const isAtProcedureLimit = procedureLimit !== null && activeProceduresCount >= procedureLimit;
    const procedureLimitMessage = isAtProcedureLimit
        ? `${planLimitText(planLabel, procedureLimit, 'procedimento', 'procedimentos')}. ${limitActionHint(false)}`
        : '';
    const reactivateLimitMessage = isAtProcedureLimit
        ? `${planLimitText(planLabel, procedureLimit, 'procedimento ativo', 'procedimentos ativos')}. ${limitActionHint(false, true)}`
        : '';

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleOpenModal = (mode, procedure = null) => {
        // No limite do plano, criar abre o aviso com o atalho de troca de plano
        if (mode === 'create' && isAtProcedureLimit) {
            setUpgradeModalOpen('create');
            return;
        }
        setModalMode(mode);
        setSelectedProcedure(procedure);
        const newFormData = procedure ? {
            label: procedure.Label,
            price: formatMoneyInput(procedure.Price),
            duration: procedure.Duration,
            description: procedure.Description || '',
            public_schedule_enabled: !!procedure.public_schedule_enabled,
            public_schedule_name: procedure.public_schedule_name || '',
            public_schedule_description: procedure.public_schedule_description || ''
        } : EMPTY_FORM;
        setFormData(newFormData);
        setIsFormValid(validateForm(newFormData));
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProcedure(null);
        setFormData(EMPTY_FORM);
        setIsFormValid(false);
    };

    // O que ainda falta para salvar, na ordem do formulário (vazio = pode salvar)
    const missingFields = (data) => [
        data.label.trim() === '' && 'o nome',
        (data.price.trim() === '' || parseMoney(data.price) === null) && 'o preço',
        (data.duration.trim() === '' || data.duration === '00:00:00') && 'a duração'
    ].filter(Boolean);

    const validateForm = (data) => missingFields(data).length === 0;

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let updatedFormData;

        if (name == 'duration') {
            // Remove qualquer caractere que não seja número ou :
            let cleanValue = value.replace(/[^\d:]/g, '');

            // Garante que tenha os : nas posições corretas
            if (cleanValue.length >= 2 && !cleanValue.includes(':')) {
                cleanValue = cleanValue.substring(0, 2) + ':' + cleanValue.substring(2);
            }
            if (cleanValue.length >= 5 && cleanValue.split(':').length < 3) {
                cleanValue = cleanValue.substring(0, 5) + ':00';
            }

            // Limita os valores de horas e minutos
            const parts = cleanValue.split(':');
            if (parts[0] && parseInt(parts[0]) > 23) parts[0] = '23';
            if (parts[1] && parseInt(parts[1]) > 59) parts[1] = '59';

            cleanValue = parts.join(':');

            updatedFormData = {
                ...formData,
                [name]: cleanValue
            };
        } else {
            updatedFormData = {
                ...formData,
                // Preço digitado como no Brasil ("1.200,99"); o envio converte com parseMoney
                [name]: name === 'price' ? cleanMoneyTyping(value) : value
            };
        }

        setFormData(updatedFormData);
        setIsFormValid(validateForm(updatedFormData));
    };

    // Campos do agendamento público: não entram na validação
    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        // Garante que a duração esteja no formato correto (HH:mm:00)
        let formattedDuration = formData.duration;
        if (formattedDuration.match(/^\d{1}:/)) {
            formattedDuration = '0' + formattedDuration;
        }
        if (formattedDuration.match(/^\d{2}:\d{1}:/)) {
            formattedDuration = formattedDuration.replace(/^(\d{2}):(\d{1}):/, '$1:0$2:');
        }

        const data = {
            label: formData.label,
            price: parseMoney(formData.price),
            duration: formattedDuration,
            description: formData.description,
            public_schedule_enabled: formData.public_schedule_enabled ? 1 : 0,
            public_schedule_name: formData.public_schedule_name,
            public_schedule_description: formData.public_schedule_description
        };

        awaitingSyncRef.current = true;
        if (modalMode == 'create') {
            SetCompanyProcedure(data, (result) => {
                handleCloseModal();
                handleSaved(result, 'Procedimento adicionado', 'create');
            });
        } else {
            UpdateCompanyProcedure({
                ...data,
                id: selectedProcedure.Id
            }, (result) => {
                handleCloseModal();
                handleSaved(result, 'Procedimento atualizado');
            });
        }
    };

    const handleOpenDeleteModal = (procedure) => {
        setProcedureToDelete(procedure);
        setShowConfirmDelete(true);
    };

    const handleCloseDeleteModal = () => {
        setShowConfirmDelete(false);
        setProcedureToDelete(null);
    };

    const handleDelete = () => {
        if (procedureToDelete) {
            awaitingSyncRef.current = true;
            DeleteCompanyProcedure(procedureToDelete.Id, (result) => {
                handleCloseDeleteModal();
                handleSaved(result, 'Procedimento inativado');
            });
        }
    };

    const handleOpenReactivateModal = (procedure) => {
        if (isAtProcedureLimit) {
            setUpgradeModalOpen('reactivate');
            return;
        }
        setProcedureToReactivate(procedure);
        setShowConfirmReactivate(true);
    };

    const handleCloseReactivateModal = () => {
        setShowConfirmReactivate(false);
        setProcedureToReactivate(null);
    };

    const handleReactivate = () => {
        if (procedureToReactivate) {
            awaitingSyncRef.current = true;
            UpdateCompanyProcedure({ id: procedureToReactivate.Id, active: 1 }, (result) => {
                handleCloseReactivateModal();
                handleSaved(result, 'Procedimento reativado', 'reactivate');
            });
        }
    };

    // Calculate pagination
    const totalPages = Math.ceil(procedures.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = procedures.slice(indexOfFirstItem, indexOfLastItem);

    const addBlockedReason = !canManageProcedures ? ADD_DENIED : procedureLimitMessage;

    return (
        <div className="page-content">
            <div className={styles.container}>
                <PlanUpgradeModal
                    isOpen={!!upgradeModalOpen}
                    onClose={() => setUpgradeModalOpen(false)}
                    featureName="procedimentos"
                    featureNameSingular="procedimento"
                    type="limit"
                    limit={procedureLimit ?? undefined}
                    hint={limitActionHint(false, upgradeModalOpen === 'reactivate')}
                />

                <div className={styles.header}>
                    <AreaTitle
                        Title="Procedimentos"
                        Description="O que você oferece, com o valor e a duração usados na agenda."
                        Icon={<MedicalServicesOutlinedIcon />}
                    />
                    <Tooltip title={addBlockedReason} arrow>
                        <span>
                            <CustomButton
                                style="main"
                                variant="contained"
                                icon={<AddIcon />}
                                label="Adicionar procedimento"
                                onClick={() => handleOpenModal('create')}
                                disabled={!canManageProcedures}
                            />
                        </span>
                    </Tooltip>
                </div>

                {isAtProcedureLimit && (
                    <FeatureLockBanner
                        type="limit"
                        lockIcon
                        featureName="procedimentos"
                        featureNameSingular="procedimento"
                        currentCount={activeProceduresCount}
                        limit={procedureLimit}
                        hint={limitActionHint(false)}
                        onUpgradeClick={() => setUpgradeModalOpen('create')}
                    />
                )}

                {!loading && procedures.length > 0 && (
                    <ProcedureOverview procedures={procedures} limit={procedureLimit} />
                )}

                <ProcedureTable
                    loading={loading}
                    procedures={procedures}
                    items={currentItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    activeCount={activeProceduresCount}
                    canManage={canManageProcedures}
                    addBlockedReason={addBlockedReason}
                    reactivateLimitMessage={reactivateLimitMessage}
                    onPageChange={handlePageChange}
                    onAdd={() => handleOpenModal('create')}
                    onEdit={(procedure) => handleOpenModal('edit', procedure)}
                    onInactivate={handleOpenDeleteModal}
                    onReactivate={handleOpenReactivateModal}
                />
            </div>

            {isModalOpen && (
                <ProcedureFormPopup
                    mode={modalMode}
                    formData={formData}
                    isFormValid={isFormValid}
                    missing={missingFields(formData)}
                    onInputChange={handleInputChange}
                    onFieldChange={handleFieldChange}
                    onSubmit={handleSubmit}
                    onClose={handleCloseModal}
                />
            )}

            <ConfirmPopup
                Visibility={showConfirmDelete}
                Title="Inativar procedimento"
                Description={`Inativar "${procedureToDelete?.Label}"? Ele sai das opções de agendamento, mas o histórico continua. Dá para reativar depois.`}
                ConfirmLabel="Inativar"
                OnConfirm={handleDelete}
                OnHide={handleCloseDeleteModal}
            />

            <ConfirmPopup
                Visibility={showConfirmReactivate}
                Title="Reativar procedimento"
                Description={`Deseja reativar o procedimento "${procedureToReactivate?.Label}"? Ele voltará a aparecer nas opções de agendamento.`}
                ConfirmLabel="Reativar"
                OnConfirm={handleReactivate}
                OnHide={handleCloseReactivateModal}
            />

            <Toast
                open={toast.open}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={6000}
            />
        </div>
    );
};

export default ProcedureList;
